import ReactGA from "react-ga4"
import { useTranslation } from "react-i18next"
import { useMutation, useQueryClient } from "react-query"
import { useNavigate } from "react-router-dom"

import * as Yup from "yup"
import CloseIcon from "@mui/icons-material/Close"
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  useMediaQuery,
  useTheme,
} from "@mui/material"
import { Button, InputText, NotificationUtils, Typography } from "@synapse-analytics/synapse-ui"
import type { AxiosError, AxiosResponse } from "axios"
import { useFormik } from "formik"

import { KonanAPI } from "../../services/KonanAPI"
import type { CreateProjectRequest, baseErrorType } from "../../types/custom/projects"
import type { Deployment } from "../../types/generated/api/Deployment"
import type { ProjectWorkflowCreationDialogProps } from "./interfaces"

/**
 * Project Workflow Creation Dialog
 * @param  {string} isOpen
 * @param  {function} onClose
 * @return  {React.ReactElement}
 */
export function ProjectWorkflowCreationDialog(props: Readonly<ProjectWorkflowCreationDialogProps>): React.ReactElement {
  const { isOpen, onClose } = props
  const { t } = useTranslation("projects")

  const MuiTheme = useTheme()
  const navigate = useNavigate()

  const queryClient = useQueryClient()

  const fullScreen = useMediaQuery(MuiTheme.breakpoints.down("md"))

  // Project form validation schema object with translations
  const projectValidationSchema = Yup.object({
    name: Yup.string()
      .max(30, t("dialogs.newWorkflowProject.validation.nameMaxLength"))
      .required(t("dialogs.newWorkflowProject.validation.nameRequired")),
    description: Yup.string().max(200, t("dialogs.newWorkflowProject.validation.descriptionMaxLength")),
    type: Yup.string().required(t("dialogs.newWorkflowProject.validation.nameRequired")),
  })

  // Request mutations
  const createProjectMutation = useMutation<AxiosResponse<Deployment>, AxiosError<baseErrorType>, CreateProjectRequest>(
    KonanAPI.createProject,
    {
      onSuccess: () => {
        NotificationUtils.toast(t("notifications.workflowProjectCreated"), { severity: "success" })
      },
      onError: ({ response }) => {
        NotificationUtils.toast(
          (response?.data as { non_field_errors: string[] } | undefined)?.non_field_errors?.[0] ??
            t("notifications.workflowProjectCreateError"),
          {
            severity: "error",
          },
        )
      },
    },
  )

  // Project creation form control
  const formik = useFormik({
    initialValues: {
      name: "",
      description: "",
      type: "classification",
      category: "workflow",
    },
    validationSchema: projectValidationSchema,
    onSubmit: async (values) => {
      try {
        ReactGA.event({
          category: "Deployments",
          action: "Creating new deployment",
        })

        await createProjectMutation.mutateAsync({
          name: values.name,
          type: values.type,
          category: values.category,
          description: values.description || undefined,
        })

        onClose()

        // navigate to workflow tab after closing dialog
        navigate("?page=workflow")
        queryClient.invalidateQueries("projects")
      } catch (e) {
        ReactGA.event({
          category: "Deployments",
          action: "Deployment contains errors",
        })
      }
    },
  })

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      fullScreen={fullScreen}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        style: {
          // To enable the date-picker to overflow
          overflow: "unset",
        },
      }}
    >
      <DialogTitle className="alt-dialog-header-base">
        <Grid container justifyContent="space-between">
          <Typography variant="h2-bold">{t("dialogs.newWorkflowProject.title")}</Typography>

          <IconButton aria-label="close" onClick={onClose} className="close-icon-button">
            <CloseIcon style={{ color: "var(--gray-background-1)" }} />
          </IconButton>
        </Grid>
      </DialogTitle>

      <DialogContent className="dialog-content-base">
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <InputText
              fullWidth
              required
              hideDescription
              id="name"
              label={t("dialogs.newWorkflowProject.fields.name")}
              placeholder={t("dialogs.newWorkflowProject.fields.namePlaceholder")}
              value={formik.values.name}
              handleChange={formik.handleChange}
              handleBlur={formik.handleBlur}
              error={formik.touched.name && Boolean(formik.errors.name) && formik.errors.name}
            />
          </Grid>

          <Grid item xs={12}>
            <InputText
              fullWidth
              hideDescription
              id="description"
              label={t("dialogs.newWorkflowProject.fields.description")}
              placeholder={t("dialogs.newWorkflowProject.fields.descriptionPlaceholder")}
              value={formik.values.description}
              handleChange={formik.handleChange}
              handleBlur={formik.handleBlur}
              error={formik.touched.description && Boolean(formik.errors.description) && formik.errors.description}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions className="alt-dialog-actions-base">
        <Button onClick={onClose} variant={"secondary"}>
          {t("dialogs.newWorkflowProject.buttons.cancel")}
        </Button>
        <Button
          onClick={formik.submitForm}
          variant="primary"
          disabled={!formik.isValid || formik.isSubmitting || !formik.values.name}
          isLoading={formik.isSubmitting}
        >
          {t("dialogs.newWorkflowProject.buttons.submit")}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
